import { z } from 'zod';

const cryptoStateAgeSchema = z.object({
  state: z.string(),
  count: z.number().int().nonnegative(),
  oldestCreatedAt: z.string().nullable(),
});

const cryptoQueueHealthSchema = z.object({
  status: z.enum(['available', 'unavailable']),
  counts: z.record(z.string(), z.number().int().nonnegative()).nullable(),
});

export const cryptoHealthSchema = z.object({
  checkedAt: z.string(),
  marketplaceId: z.string(),
  merchantId: z.string(),
  wallets: z.array(cryptoStateAgeSchema),
  callbacks: z.array(cryptoStateAgeSchema),
  unattributedCallbacks: z
    .object({
      scope: z.literal('merchant'),
      merchantId: z.string(),
      sharedMarketplaceIds: z.array(z.string()),
      count: z.number().int().nonnegative(),
      states: z.array(cryptoStateAgeSchema),
      lastCallbackAt: z.string().nullable(),
    })
    .optional(),
  deliveries: z.array(cryptoStateAgeSchema),
  withdrawals: z.array(cryptoStateAgeSchema),
  freshness: z.object({
    lastCallbackAt: z.string().nullable(),
    lastVerifiedCallbackCreatedAt: z.string().nullable(),
    lastVerifiedAt: z.string().nullable(),
  }),
  providerBalances: z.object({
    status: z.enum(['available', 'unavailable']),
    checkedAt: z.string(),
    lastSuccessfulAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    balances: z.array(z.object({ amount: z.string(), currency: z.string() })).nullable(),
    source: z.string(),
    scope: z.literal('merchant'),
    sharedMarketplaceIds: z.array(z.string()),
  }),
  queues: z.object({
    scope: z.literal('all_gateway_marketplaces'),
    verification: cryptoQueueHealthSchema,
    delivery: cryptoQueueHealthSchema,
    withdrawal: cryptoQueueHealthSchema.optional(),
  }),
});

export type CryptoHealth = z.infer<typeof cryptoHealthSchema>;
export type CryptoStateAge = z.infer<typeof cryptoStateAgeSchema>;
export type CryptoQueueHealth = z.infer<typeof cryptoQueueHealthSchema>;

export const cryptoCurrencySettingsSchema = z.object({
  currency: z.string().min(1),
  expectedVersion: z.string().max(100).optional(),
  depositEnabled: z.boolean(),
  withdrawalEnabled: z.boolean(),
  reason: z.string().trim().min(5, 'Enter a reason of at least 5 characters').max(500),
});

export const cryptoCurrencyFormSchema = cryptoCurrencySettingsSchema.extend({
  confirmed: z.boolean().refine(Boolean, 'Confirm the currency changes'),
});
export type CryptoCurrencyForm = z.infer<typeof cryptoCurrencyFormSchema>;
export type CryptoCurrencySettings = z.infer<typeof cryptoCurrencySettingsSchema>;

export const cryptoClientCommandSchema = z.object({
  action: z.enum(['enable', 'disable']),
  reason: z.string().trim().min(5, 'Enter a reason of at least 5 characters').max(500),
  confirmed: z.boolean().refine(Boolean, 'Confirm the full client scope'),
});
export type CryptoClientCommand = z.infer<typeof cryptoClientCommandSchema>;

export const cryptoWalletFilterSchema = z.object({
  currency: z.string().trim().max(100),
  state: z.enum(['', 'new', 'unknown', 'ready']),
  merchantId: z.string().trim().max(200),
  playerId: z.string().trim().max(200),
  clientId: z.union([z.literal(''), z.uuid()]),
  address: z.string().trim().max(200),
});
export type CryptoWalletFilters = z.infer<typeof cryptoWalletFilterSchema>;
